import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomTextField from "@/components/input/PencomTextField";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useNavigate } from "react-router-dom";
import { type LoginForm } from "@/forms/auth/auth.form";
import { useForm } from "@tanstack/react-form";
import { useInitiateAuth } from "@/hooks/auth/auth.hook";
import { AuthStatus } from "@/constants/enums/auth-status.enum";
import { isEmail, validateEmployerCodeOrEmail } from "@/utils/authUtils";
import { useEffect } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";
import { env } from "@/utils/env";

const LoginPage = () => {
  const navigate = useNavigate();
  const { trackEvent, eventNames } = useAnalytics();
  const {
    mutate: initiateAuth,
    isPending: isInitiatingAuth,
    error: initiateAuthError,
    reset: resetInitiateAuth,
  } = useInitiateAuth();

  console.log("current env:", env.APP_ENV);

  const handleInitiateAuth = (
    employerCode?: string | null,
    emailAddress?: string | null
  ) => {
    const trimmedEmail = emailAddress?.trim() || undefined;
    initiateAuth(
      { employerCode: employerCode || undefined, email: trimmedEmail },
      {
        onSuccess: (data) => {
          console.log("data", data);
          if (data.data.status === AuthStatus.REGISTER) {
            navigate(PencomRoutes.VERIFY_COMPANY_DETAILS, { state: data });
          } else if (data.data.status === AuthStatus.AUTHORIZED_CONTACT) {
            // Completed employer with no authorized contact — send them to the
            // add-authorized-contact step to finish setting up before login.
            navigate(PencomRoutes.EMPLOYER_REP_DETAILS, {
              state: { employerCode: data?.data?.user?.employerCode },
            });
          } else if (data.data.status === AuthStatus.VERIFY) {
            console.log({ data });
            navigate(PencomRoutes.VERIFICATION, { state: data?.data?.user });
          }
        },
      }
    );
  };

  const form = useForm({
    defaultValues: {
      employerCode: "",
      emailAddress: "",
    } as LoginForm,
    onSubmit: async ({ value }) => {
      const input = value.employerCode?.trim() || "";

      // Determine if input is email or employer code
      if (isEmail(input)) {
        handleInitiateAuth(undefined, input);
      } else {
        handleInitiateAuth(input, undefined);
      }
    },
  });

  const handleRegisterHere = () => {
    navigate(PencomRoutes.EMPLOYER_CODE_REG);
  };

  useEffect(() => {
    trackEvent(eventNames.onboardingPageView, {
      step: "login",
    });
  }, []);

  return (
    <div className="w-full flex flex-col">
      <h2 className="font-clash-display font-semibold text-3xl md:text-[40px] text-medium-dark-grey leading-[1.25] tracking-[-0.6px]">
        PenCom Employer Hub
      </h2>
      <p className="font-clash-display font-medium text-base md:text-[20px] text-medium-dark-grey leading-6 tracking-[-0.1px] mt-1">
        Apply for PCC and monitor your compliance status.
      </p>

      <div className="flex flex-col gap-10 mt-7.5">
        <p className="font-clash-display text-md text-medium-dark-grey">
          <span className="font-normal text-black-dark-grey">
            New Employer?
          </span>{" "}
          <button
            type="button"
            className="font-clash-display font-semibold text-primary-button/80 underline decoration-solid cursor-pointer hover:opacity-90"
            onClick={handleRegisterHere}
          >
            Apply for an Employer Code
          </button>
        </p>

        {/* Temporarily disabled — see PR discussion.
        <div
          className="flex flex-col gap-y-2.5 rounded-lg p-4 border-l-4 font-clash-display text-md leading-5 tracking-[-0.06px]"
          style={{
            backgroundColor: "var(--color-pencom-light-red)",
            borderColor: "var(--color-blood-red)",
          }}
        >
          <div className="flex items-center gap-2">
            <img
              src="/assets/icons/shield-alert.svg"
              alt=""
              className="w-4 h-4 shrink-0"
              aria-hidden
            />
            <p
              className="font-semibold uppercase text-xs tracking-[0.4px]"
              style={{ color: "var(--color-blood-red)" }}
            >
              Important Notice
            </p>
          </div>
          <p
            className="font-normal pl-6"
            style={{ color: "var(--color-black-dark-grey)" }}
          >
            Do not pay any PenCom staff or representative to facilitate your
            certificate. If anyone demands money, gifts, or any form of
            gratification, report it immediately to{" "}
            <a
              href="mailto:whistleblowing@pencom.com.ng"
              className="font-semibold underline decoration-solid"
              style={{ color: "var(--color-blood-red)" }}
            >
              whistleblowing@pencom.com.ng
            </a>
            .
          </p>
          <p
            className="font-normal pl-6"
            style={{ color: "var(--color-medium-grey)" }}
          >
            PenCom has zero tolerance for corruption.
          </p>
        </div>
        */}

        <div className="flex flex-col gap-2">
          <form.Field
            name="employerCode"
            validators={{
              onSubmit: ({ value }) => validateEmployerCodeOrEmail(value),
            }}
            children={(field) => (
              <PencomTextField
                bg="white"
                id="employerCode"
                label="Employer Code or Registered Email"
                placeholder="PR00137337 or example@example.com"
                type="text"
                value={field.state.value || ""}
                onChange={(e) => {
                  if (initiateAuthError) resetInitiateAuth();
                  field.handleChange(e.target.value);
                }}
                error={!!field.state.meta.errors.length}
                helperText={field.state.meta.errors.join(", ")}
              />
            )}
          />
          {initiateAuthError?.message && (
            <div className="flex items-center gap-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-md text-red-600 font-clash-display">
                {initiateAuthError.message}
              </p>
            </div>
          )}
          <div className="bg-white border border-stroke rounded-lg p-4 flex gap-2 items-start">
            <img
              src="/assets/icons/info-outline.svg"
              alt=""
              className="w-4 h-4 shrink-0 mt-0.5"
              aria-hidden
            />
            <div className="font-clash-display font-normal text-md text-medium-grey leading-[18px] tracking-[-0.06px]">
              <p className="mb-0">
                Your Employer Code is your company&apos;s unique PenCom
                identifier.
              </p>
              <p>
                It is required to apply for PCC and access your compliance
                records.
              </p>
            </div>
          </div>
        </div>

        <form.Subscribe
          children={(state) => (
            <PrimaryButton
              disabled={!state.canSubmit || isInitiatingAuth}
              className="w-full"
              title={
                isInitiatingAuth ? "Validating....." : "Proceed to Verification"
              }
              onClick={() => {
                trackEvent(eventNames.onboardingStepSubmitted, {
                  step: "login",
                });
                form.handleSubmit();
              }}
              isLoading={isInitiatingAuth}
            />
          )}
        />

        <p className="font-clash-display text-md text-medium-grey text-center -mt-4">
          New here?{" "}
          <button
            type="button"
            onClick={() => navigate(PencomRoutes.TUTORIALS)}
            className="font-clash-display font-semibold text-primary-button underline decoration-solid cursor-pointer hover:opacity-90"
          >
            Watch Tutorials
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
